import { Injectable } from '@angular/core';
import {
    ErrorCodesApiService,
    PartialErrorCodeWithTimestamps,
    Severity,
} from '@kempoweroyj/ngx-error-codes-api-client';
import { cloneDeep as _cloneDeep } from 'lodash';
import { PowerModuleDetails } from '@kempoweroyj/ngx-charging-stations-api-client';
import { extractPmcCanId } from 'shared/dialogs/cabinet-dialog/pmc-list/pmc-list.component';
type PartialErrorCode = PartialErrorCodeWithTimestamps;

/**
 * Interface for error information
 */
export interface ErrorInfo extends PartialErrorCode {
    title?: string; // Title for display purposes
    deviceSeverity?: string; // from Device alerts (e.g BROKEN, PARTIALLY_BROKEN)
    relatedIds?: string[];
    groupedCabinetChannels?: string[]; // Grouped cabinet/channel info (e.g., "MC 1B, 3B")
}

export interface SeverityIcon {
    svgIcon: string;
    class: string;
}

export interface AlertDetails {
    parentInfo: {
        cabinet: string;
        channel: string;
    };
    canAddress: string;
    relatedId: string;
    alerts: PowerModuleDetails['alerts'];
}

/**
 * Interface for processed alert error information
 */
export interface ProcessedAlertErrors {
    mainError: ErrorInfo;
    childErrors: ErrorInfo[];
    isPMCAlert: boolean;
    relatedIds: string[];
    cabinetChannelInfo: string;
    alertDetails: AlertDetails[];
}

// Severity options based on API values
export const severityDefinition: Array<{
    value: Severity;
    label: string;
    shortlabel: string;
    svgIcon: string;
    styleClass: string;
}> = [
    {
        value: 'unknown',
        label: 'Severity unknown, contact support if unsure',
        shortlabel: 'Unknown',
        svgIcon: 'm-alert-circle-outline',
        styleClass: 'severity-unknown',
    },
    {
        value: 'info',
        label: 'No action required, status will clear on its own',
        shortlabel: 'No action required',
        svgIcon: 'm-alert-circle',
        styleClass: 'severity-info',
    },
    {
        value: 'warning',
        label: 'Remote investigation required',
        shortlabel: 'Remote investigation',
        svgIcon: 'error-severity-warning',
        styleClass: 'severity-warning',
    },
    {
        value: 'major',
        label: 'Onsite investigation required or hardware replacement necessary',
        shortlabel: 'Onsite investigation',
        svgIcon: 'error-severity-error',
        styleClass: 'severity-major',
    },
];

/**
 * PMC-related alert types that require device-specific processing
 */
export const PMC_RELATED_ALERTS = [
    'pmc-error', // Power Module Controller error
] as const;

/**
 * Service for handling alert problems and providing standardized error information
 */
@Injectable({
    providedIn: 'root',
})
export class AlertProblemService {
    private errorCodeCache: { [key: string]: ErrorInfo } = {};
    private lastCacheUpdate: number = 0;
    private cacheLifetimeMs: number = 60000; // Cache valid for 1 minute

    constructor(private readonly errorCodesApi: ErrorCodesApiService) {}

    /**
     * Get error information for a specific problem code
     * @param problemCode The problem code to get information for
     * @returns Error information including cause and fix recommendations
     */
    async getErrorInfo(vendorCode: string, problemCode: string): Promise<ErrorInfo> {
        // Refresh cache if needed
        await this.refreshCache(vendorCode);

        // Check if error code exists in cache
        const existingError = this.errorCodeCache[problemCode];

        if (existingError) {
            const { relatedIds: _relatedIds, ...info } = existingError;

            return {
                ...info,
            };
        }

        // If not in cache
        return {
            code: problemCode,
            severity: 'unknown',
            vendor: vendorCode,
        };
    }

    /**
     * Group device alerts by error code to combine related issues
     * @param deviceAlerts Array of device alert data
     * @returns Promise with array of grouped error information
     */
    async groupDeviceAlerts(
        vendorCode: string,
        deviceAlerts: Array<{
            canAddress: string;
            relatedId: string;
            parentInfo?: {
                cabinet: string;
                channel: string;
            };
            alerts: Array<{
                problem: string;
                deviceSeverity?: string;
            }>;
        }>
    ): Promise<ErrorInfo[]> {
        if (!deviceAlerts || deviceAlerts.length === 0) return [];

        // Refresh the cache first
        await this.refreshCache(vendorCode);

        // Map to store grouped errors by errorCode
        const errorMap: { [key: string]: ErrorInfo & { _cabinetChannelMap?: { [cabinet: string]: Set<string> } } } = {};

        // Process and group device alerts
        for (const device of deviceAlerts) {
            if (!device.alerts || device.alerts.length === 0) continue;

            // Keep the original relatedId (serial number)
            const serialNumber = device.relatedId || 'Unknown Device';

            for (const alert of device.alerts) {
                if (!alert.problem) continue;

                // Get error info from cache or API
                const errorInfo = await this.getErrorInfo(vendorCode, alert.problem);

                // If this error code already exists, update both arrays
                if (errorMap[alert.problem]) {
                    const existingError = errorMap[alert.problem];

                    // Add serial number to relatedIds if not already present
                    if (!existingError.relatedIds.includes(serialNumber)) {
                        existingError.relatedIds.push(serialNumber);
                    }

                    // Add parent info for grouping if available
                    if (device.parentInfo) {
                        if (!existingError._cabinetChannelMap) {
                            existingError._cabinetChannelMap = {};
                        }
                        if (!existingError._cabinetChannelMap[device.parentInfo.cabinet]) {
                            existingError._cabinetChannelMap[device.parentInfo.cabinet] = new Set();
                        }
                        existingError._cabinetChannelMap[device.parentInfo.cabinet].add(device.parentInfo.channel);
                    }
                } else {
                    // Create a new entry for this error code
                    const newError: any = {
                        ...errorInfo,
                        relatedIds: [serialNumber],
                    };

                    if (device.parentInfo) {
                        newError._cabinetChannelMap = {
                            [device.parentInfo.cabinet]: new Set([device.parentInfo.channel]),
                        };
                    }

                    errorMap[alert.problem] = newError;
                }
            }
        }

        // Process the cabinet/channel maps to create grouped strings
        Object.values(errorMap).forEach((error) => {
            if (error._cabinetChannelMap) {
                error.groupedCabinetChannels = this.formatCabinetChannelMap(error._cabinetChannelMap);
                delete error._cabinetChannelMap; // Clean up temporary field
            }
        }); // Return all grouped errors as an array, sorted by error code
        return Object.values(errorMap).sort((a, b) => a.code.localeCompare(b.code));
    }

    /**
     * Format cabinet/channel information with consistent formatting
     * @param cabinetChannelMap Map of cabinets to their channels
     * @returns Array of formatted strings like ["MC 1A, 1B, 2B", "SC1 1A, 1B, 2A, 2B"]
     */
    private formatCabinetChannelMap(cabinetChannelMap: { [cabinet: string]: Set<string> }): string[] {
        const groupedIds: string[] = [];

        // Sort cabinets alphabetically for consistent ordering
        const sortedCabinets = Object.keys(cabinetChannelMap).sort();

        sortedCabinets.forEach((cabinet) => {
            const channelsSet = cabinetChannelMap[cabinet];
            const channels = Array.from(channelsSet).sort();
            if (channels.length > 0) {
                groupedIds.push(`${cabinet} ${channels.join(', ')}`);
            }
        });

        return groupedIds;
    }

    /**
     * Format cabinet/channel information for display with pipe separators
     * @param groupedCabinetChannels Array of strings like ["MC 1A, 1B, 2B", "SC1 1A, 1B, 2A, 2B"]
     * @returns Formatted string like "MC 1A, 1B, 2B | SC1 1A, 1B, 2A, 2B"
     */
    formatCabinetChannelInfo(groupedCabinetChannels: string[] | undefined): string {
        if (!groupedCabinetChannels || groupedCabinetChannels.length === 0) {
            return '';
        }

        // The array should already be sorted from formatCabinetChannelMap, but ensure consistency
        const sortedGroups = [...groupedCabinetChannels].sort();

        return sortedGroups.join(' | ');
    }

    /**
     * Refreshes the error code cache
     * @returns Promise that resolves when cache is refreshed
     */
    private async refreshCache(vendorCode: string, forced: boolean = false): Promise<void> {
        const now = Date.now();

        // Only refresh if cache is stale
        if (forced || now - this.lastCacheUpdate > this.cacheLifetimeMs) {
            try {
                const response = await this.errorCodesApi.getErrorCodes(vendorCode);
                const errorCodes = response?.errorCodes || [];
                if (errorCodes.length > 0) {
                    // Create a new cache object
                    const newCache: { [key: string]: any } = {};

                    // Populate cache with API data
                    errorCodes.forEach((error) => {
                        // Store with original key, keeping the original structure
                        newCache[error.code] = error;
                        error.alias?.forEach((alias: string) => {
                            newCache[alias] = error; // Also store aliases
                        });
                    });

                    // Replace the old cache
                    this.errorCodeCache = newCache;
                    this.lastCacheUpdate = now;
                }
            } catch (error) {
                console.error('Failed to refresh error code cache:', error);
            }
        }
    }

    /**
     * Get a list of all known problem codes
     * @returns Array of known problem codes from the API
     */
    async fetchAllErrorCodesForVendor(vendorCode: string): Promise<string[]> {
        // Refresh cache if needed
        await this.refreshCache(vendorCode);

        // Return all error codes from cache
        return Object.keys(this.errorCodeCache);
    }

    async getCache(vendorCode: string): Promise<{ [key: string]: ErrorInfo }> {
        // refresh cache if needed
        await this.refreshCache(vendorCode);

        // deep clone for immutability
        const cloned = _cloneDeep(this.errorCodeCache);

        // return
        return cloned;
    }

    /**
     * Check if an alert type is PMC-related and requires device-specific processing
     * @param alertType The alert type to check
     * @returns True if the alert type is PMC-related
     */
    isPMCRelatedAlert(alertType: string): boolean {
        return PMC_RELATED_ALERTS.includes(alertType as any);
    }

    /**
     * Extract alert details from station devices (shared logic from both components)
     * @param devices The station devices to process
     * @returns Array of alert details
     */
    extractAlertDetailsFromDevices(devices: any[]): AlertDetails[] {
        if (!devices || devices.length === 0) {
            return [];
        }

        return devices
            .filter(
                (d) =>
                    d?.stale !== true && // Exclude stale devices (sometimes stale field is missing)
                    (d?.details as PowerModuleDetails)?.alerts?.some(
                        (a) => a?.severity === 'BROKEN' || a?.severity === 'PARTIALLY_BROKEN'
                    )
            )
            .map((d) => {
                const details = d?.details as PowerModuleDetails;
                const { cabLabel, label } = extractPmcCanId(+details?.can);
                return {
                    canAddress: details?.can,
                    parentInfo: {
                        cabinet: cabLabel,
                        channel: label,
                    },
                    relatedId: details?.moduleSerialNumber || d.serialNumber,
                    alerts:
                        details?.alerts?.filter(
                            (a) => a?.severity === 'BROKEN' || a?.severity === 'PARTIALLY_BROKEN'
                        ) || [],
                };
            });
    }

    /**
     * Get the highest severity from an array of error infos
     * @param errorInfos Array of error information objects
     * @returns The highest severity level
     */
    getHighestSeverity(errorInfos: ErrorInfo[]): Severity {
        if (!errorInfos || errorInfos.length === 0) {
            return 'unknown'; // Default fallback
        }

        // Priority order: error (highest) > warning > ok > unknown (lowest)
        const severities = errorInfos.map((error) => error.severity?.toLowerCase());

        if (severities.includes('critical')) {
            return 'critical';
        } else if (severities.includes('major')) {
            return 'major';
        } else if (severities.includes('warning')) {
            return 'warning';
        } else if (severities.includes('info')) {
            return 'info';
        } else {
            return 'unknown';
        }
    }

    /**
     * Get severity icon configuration for a given severity level
     * @param severity The severity level
     * @returns Icon configuration object
     */
    getSeverityIcon(severity: Severity): SeverityIcon {
        const iconDef = severityDefinition.find((s) => s.value === severity);

        if (iconDef) {
            return {
                svgIcon: iconDef.svgIcon,
                class: iconDef.styleClass,
            };
        }

        // Default to unknown severity icon
        return {
            svgIcon: 'm-alert-circle',
            class: 'severity-unknown',
        };
    }

    /**
     * Get severity message for a given severity level
     * @param severity The severity level
     * @returns Human-readable severity message
     */
    getSeverityMessage(severity: Severity): string {
        const sevDef = severityDefinition.find((s) => s.value === severity);

        if (sevDef) {
            return sevDef.label;
        }

        // Default to unknown severity message
        return 'Severity unknown, contact support if unsure';
    }

    /**
     * Process station devices to get alert severity
     * @param devices Station devices array
     * @returns Promise with the highest device severity or null if no device alerts
     */
    async getHighestDeviceSeverityFromDevices(vendorCode: string, devices: any[]): Promise<Severity | null> {
        try {
            const alertDetails = this.extractAlertDetailsFromDevices(devices);

            if (!alertDetails || alertDetails.length === 0) {
                return null;
            }

            // Convert to format expected by groupDeviceAlerts
            const formattedAlertDetails = alertDetails.map((detail) => ({
                canAddress: detail.canAddress,
                relatedId: detail.relatedId,
                parentInfo: detail.parentInfo,
                alerts: detail.alerts.map((alert) => ({
                    problem: alert.problem || '',
                    deviceSeverity: alert.severity,
                })),
            }));

            // Use existing groupDeviceAlerts method
            const errorInfos = await this.groupDeviceAlerts(vendorCode, formattedAlertDetails);

            if (!errorInfos || errorInfos.length === 0) {
                return null;
            }

            // Return the highest severity
            return this.getHighestSeverity(errorInfos);
        } catch (error) {
            console.error('Failed to get highest device severity from devices:', error);
            return null;
        }
    }

    /**
     * Check if an error code contains comma-separated values
     * @param errorCode The error code to check
     * @returns True if the error code contains commas
     */
    private isCommaSeparatedErrorCode(errorCode: string): boolean {
        return errorCode && errorCode.includes(',');
    }

    /**
     * Parse comma-separated error codes into individual codes
     * @param errorCode The comma-separated error code
     * @returns Array of individual error codes
     */
    private parseCommaSeparatedErrorCodes(errorCode: string): string[] {
        return errorCode
            .split(',')
            .map((code) => code.trim())
            .filter((code) => code.length > 0);
    }

    /**
     * Process comma-separated error codes and create child errors
     * @param vendorCode The vendor code
     * @param errorCode The potentially comma-separated error code
     * @returns Promise with array of child error information
     */
    async processCommaSeparatedErrorCodes(vendorCode: string, errorCode: string): Promise<ErrorInfo[]> {
        if (!errorCode || !this.isCommaSeparatedErrorCode(errorCode)) {
            return [];
        }

        const individualCodes = this.parseCommaSeparatedErrorCodes(errorCode);

        if (individualCodes.length <= 1) {
            return [];
        }

        // Create child errors for each individual error code
        const childErrors = await Promise.all(
            individualCodes.map(async (code) => {
                const errorInfo = await this.getErrorInfo(vendorCode, code);
                return {
                    ...errorInfo,
                    relatedIds: [],
                    groupedCabinetChannels: [],
                };
            })
        );

        return childErrors;
    }

    /**
     * Get combined cabinet/channel information from alert details
     * @param alertDetails Array of alert details
     * @returns Combined string of all unique cabinet/channel combinations
     */
    getCombinedCabinetChannelsInfo(alertDetails: AlertDetails[]): string {
        if (!alertDetails || alertDetails.length === 0) {
            return '';
        }

        // Collect all cabinet/channel combinations from alertDetails
        const cabinetChannelMap: { [cabinet: string]: Set<string> } = {};

        alertDetails.forEach((detail) => {
            if (detail.parentInfo && detail.parentInfo.cabinet && detail.parentInfo.channel) {
                const cabinet = detail.parentInfo.cabinet;
                const channel = detail.parentInfo.channel;

                if (!cabinetChannelMap[cabinet]) {
                    cabinetChannelMap[cabinet] = new Set();
                }
                cabinetChannelMap[cabinet].add(channel);
            }
        });

        // Create formatted strings from the collected data with alphabetical cabinet sorting
        const groupedIds: string[] = [];

        // Sort cabinets alphabetically
        const sortedCabinets = Object.keys(cabinetChannelMap).sort();

        sortedCabinets.forEach((cabinet) => {
            const channelsSet = cabinetChannelMap[cabinet];
            const channels = Array.from(channelsSet).sort();
            if (channels.length > 0) {
                groupedIds.push(`${cabinet} ${channels.join(', ')}`);
            }
        });

        return this.formatCabinetChannelInfo(groupedIds);
    }

    /**
     * Process alert and return both main error info and child errors
     * @param vendorCode The vendor code
     * @param alert The alert object
     * @param station The station data (optional, for PMC alerts)
     * @param alertDetails The alert details (optional, for PMC alerts)
     * @returns Promise with processed alert information
     */
    async processAlertErrors(
        vendorCode: string,
        alert: any,
        station?: any,
        alertDetails?: AlertDetails[]
    ): Promise<ProcessedAlertErrors> {
        const isPMCAlert = this.isPMCRelatedAlert(alert.type);

        let childErrors: ErrorInfo[] = [];

        if (isPMCAlert && station?.devices) {
            // Handle PMC alerts with device-specific errors
            const extractedDetails = alertDetails || this.extractAlertDetailsFromDevices(station.devices);

            if (extractedDetails.length > 0) {
                const formattedDetails = extractedDetails.map((detail) => ({
                    canAddress: detail.canAddress,
                    relatedId: detail.relatedId,
                    parentInfo: detail.parentInfo,
                    alerts: detail.alerts.map((deviceAlert) => ({
                        problem: deviceAlert.problem || '',
                        deviceSeverity: deviceAlert.severity,
                    })),
                }));

                childErrors = await this.groupDeviceAlerts(vendorCode, formattedDetails);
            }
        } else if (alert.errorCode) {
            // Handle comma-separated error codes for non-PMC alerts
            childErrors = await this.processCommaSeparatedErrorCodes(vendorCode, alert.errorCode);
        }

        // Get main error info
        const mainError = await this.getErrorInfo(vendorCode, alert.errorCode || '');

        // Get related IDs from alert details (for PMC alerts)
        const relatedIds = (alertDetails || [])
            .map((detail) => detail.relatedId)
            .filter((relatedId) => relatedId && relatedId !== 'undefined' && relatedId.trim() !== '');

        // Get formatted cabinet/channel info (for PMC alerts)
        const cabinetChannelInfo = this.getCombinedCabinetChannelsInfo(alertDetails || []);

        return {
            mainError: {
                ...mainError,
                description: mainError.description || alert.message,
                title: alert.title || 'Unknown Alert',
            },
            childErrors,
            isPMCAlert,
            relatedIds,
            cabinetChannelInfo,
            alertDetails: isPMCAlert ? alertDetails : [],
        };
    }
}
